import { useState } from "react";
import { useNotificationContext } from "contexts/NotificationContext";
import { diagnoseDriverApi, resetDriverLocationApi, autoCleanupStuckDriversApi } from "api/location";
import { IStuckDriverDiagnosis, IResetDriverLocationInput } from "models/location";

export default function useStuckDrivers() {
    const { showError, showSuccess } = useNotificationContext();
    const [loading, setLoading] = useState(false);
    const [diagnosis, setDiagnosis] = useState<IStuckDriverDiagnosis | null>(null);

    // Function to trigger dashboard refresh
    const triggerDashboardRefresh = (resetDriverId?: string) => {
        // Dispatch a custom event that the dashboard can listen to
        window.dispatchEvent(new CustomEvent('refreshDriverLocations', {
            detail: { resetDriverId }
        }));
    };

    const diagnoseDriver = async (driverId: string) => {
        setLoading(true);
        try {
            const { data, error } = await diagnoseDriverApi(driverId);
            if (data) {
                // Handle both response formats: standard API response and direct data
                const diagnosisData = data.data || data;
                setDiagnosis(diagnosisData);
                showSuccess("Driver diagnosis completed");
                return diagnosisData;
            } else {
                showError(error?.message || "Failed to diagnose driver");
                return null;
            }
        } catch (error) {
            console.error("Exception diagnosing driver:", error);
            showError("An error occurred while diagnosing the driver");
            return null;
        } finally {
            setLoading(false);
        }
    };

    const resetDriverLocation = async (input: IResetDriverLocationInput) => {
        setLoading(true);
        try {
            console.log("🔄 Resetting driver location:", {
                driver_id: input.driver_id,
                latitude: input.latitude,
                longitude: input.longitude,
                heading: input.heading
            });

            const { data, error } = await resetDriverLocationApi(input);

            if (data) {
                console.log("✅ Driver location reset successful");
                showSuccess("Driver location reset successfully. Dashboard map will refresh automatically within 4 seconds.");

                // Refresh diagnosis after reset
                if (diagnosis) {
                    console.log("🔄 Refreshing diagnosis after reset");
                    const updatedDiagnosis = await diagnoseDriver(input.driver_id);
                    console.log("📊 Post-reset diagnosis:", {
                        driver_id: updatedDiagnosis?.driver_id,
                        current_location: updatedDiagnosis?.current_location,
                        is_stuck: updatedDiagnosis?.is_stuck,
                        issues: updatedDiagnosis?.issues,
                        recent_updates: updatedDiagnosis?.last_5_updates?.length
                    });
                }

                // Trigger dashboard refresh to update location data
                console.log("🔄 Triggering dashboard location refresh for driver:", input.driver_id);
                triggerDashboardRefresh(input.driver_id);

                return true;
            } else {
                console.error("❌ Reset failed:", error);

                // Show more detailed error message if available
                let errorMessage = "Failed to reset driver location";
                if (error?.message) {
                    errorMessage = error.message;
                }
                // Check for additional detail information (backend may provide extra fields)
                const errorObj = error as any;
                if (errorObj?.detail) {
                    errorMessage += ` (${errorObj.detail})`;
                }

                showError(errorMessage);
                return false;
            }
        } catch (error) {
            console.error("💥 Exception resetting driver location:", error);
            showError("An error occurred while resetting the driver location");
            return false;
        } finally {
            setLoading(false);
        }
    };

    const autoCleanupStuckDrivers = async (thresholdMinutes?: number) => {
        setLoading(true);
        try {
            const { data, error } = await autoCleanupStuckDriversApi(thresholdMinutes);
            if (data) {
                showSuccess(`Fleet cleanup completed: ${data.total_stuck_found || 0} stuck drivers were forced offline.`);
                triggerDashboardRefresh();
                return data;
            } else {
                showError(error?.message || "Failed to run fleet cleanup");
                return null;
            }
        } catch (error) {
            console.error("Exception in fleet cleanup:", error);
            showError("An error occurred while running fleet cleanup");
            return null;
        } finally {
            setLoading(false);
        }
    };

    const clearDiagnosis = () => {
        setDiagnosis(null);
    };

    return {
        loading,
        diagnosis,
        diagnoseDriver,
        resetDriverLocation,
        autoCleanupStuckDrivers,
        clearDiagnosis,
    };
}

